// Hand-written shell for DSL-generated entity List Views.
//
// Every generated `src/<module>/<Entity>ListView.tsx` builds only its data columns and renders
// this shell, which owns the invariant scaffolding ONCE instead of inlining it into ~56 files:
// the actions column, server-side data source (paging / grouping / aggregation), delete / refresh
// / export handlers, field-restriction filtering, toolbar and DataGrid layout.
//
// Behaviour is identical to the previously-inlined generated code — do NOT change data-source
// wiring, export request shape, navigation, or field-restriction semantics without also updating
// the generator (`tools/cli/src/generators/react/list-view.ts`), the characterization test
// (`src/components/__tests__/EntityListView.characterization.test.tsx`), and the golden snapshots.

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import {
  DataGridPremium,
  GridColDef,
  GridActionsCellItem,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import type {
  GridRowId,
  GridDataSource,
  GridAggregationFunctionDataSource,
  GridRowSelectionModel,
  GridGetRowsError,
  GridUpdateRowError,
} from '@mui/x-data-grid-premium';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Add from '@mui/icons-material/Add';
import EditNote from '@mui/icons-material/EditNote';
import { Box, Button, Chip } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ViewToolbar } from '@/components/ViewToolbar';
import { ListViewExtras } from '@/config/listViewExtras';
import { ListViewMoreMenu } from '@/components/ListViewMoreMenu';
import { GridViewSelector } from '@/components/GridViewSelector';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CommandResultSnackbar, type CommandResult } from '@/components/CommandResultSnackbar';
import { BulkEditDialog } from '@/components/bulkEdit/BulkEditDialog';
import { BulkCommandDialog } from '@/components/bulkEdit/BulkCommandDialog';
import { BulkCommandsMenu } from '@/components/bulkEdit/BulkCommandsMenu';
import { bulkEditOutcome, type BulkEditConfig, type BulkEditSummary } from '@/lib/bulkEdit';
import { bulkCommandOutcome, bulkCommandRun, type BulkCommandAvailability, type BulkCommandConfig } from '@/lib/bulkCommand';
import { NewTemplateSplitButton } from '@/components/templates/NewTemplateSplitButton';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationErrorPanel } from '@/components/forms/ValidationErrorPanel';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import { apiClient, getErrorMessage } from '@/lib/apiClient';
import { saveBlobResponse } from '@/lib/fileDownload';
import {
  createEntityListDataSource,
  filterRestrictedColumns,
  buildExportRequestParams,
  resolveSelectionIds,
  resolveSelectedRows,
  editTarget,
  listReturnTo,
  isTypingInList,
} from '@/components/entityListViewRuntime';
import { useEnumColumns } from '@/lib/enumGridFilters';
import { hoverRevealGridSx, GRID_ROW_HEIGHT } from '@/lib/gridStyles';
import { useGroupExpansionPersistence } from '@/lib/useGroupExpansionPersistence';
import { useFieldRestrictions } from '@/core/hooks/useFieldRestrictions';
import type { EntityService, ReadOnlyEntityService } from '@/lib/EntityService';

// Stable empty map so `useEnumColumns` runs unconditionally (0 enum hooks) for entities with no
// enum columns — keeps the hook order constant without a conditional call.
const EMPTY_ENUM_TYPES: Record<string, string> = {};

const EMPTY_SELECTION: GridRowSelectionModel = { type: 'include', ids: new Set() };

/**
 * A toolbar action that operates on the currently selected rows. The built-in bulk
 * Delete is the first of these; per-entity actions plug in via
 * {@link EntityListViewProps.bulkActions}.
 */
export interface BulkAction {
  /** Stable key, e.g. `delete`. */
  key: string;
  /** Toolbar button label (already translated). */
  label: string;
  icon?: ReactNode;
  /** When provided, `window.confirm` with this text gates execution (matches row-delete UX). */
  confirmMessage?: (selectedCount: number) => string;
  /**
   * Runs the action against the selected record ids. `rows` are the selected rows the grid holds,
   * for an action that names a row in its outcome (commandPerRow.ts); a row no longer loaded is
   * absent, the ids stay the authority on what was picked.
   */
  execute: (ids: string[], rows: Record<string, unknown>[]) => Promise<void>;
  /** Clear selection + refresh the grid after success (default true). */
  refreshAfter?: boolean;
}

export interface EntityListViewProps<T> {
  /** Owning module (e.g. `Core`) — for field restrictions. */
  module: string;
  /** Entity name (e.g. `NumberSeries`) — for field restrictions + delete-confirm label. */
  entity: string;
  /** The Phase-1 service singleton this view binds to. Create/Update types are irrelevant here
   *  (the shell only reads + deletes), so they're left open. */
  /**
   * Read-only services are accepted: a system-maintained entity (ui.readOnly) has no
   * create/update/delete to offer, and its grid drops New and Delete accordingly.
   */
  service: EntityService<T, any, any> | ReadOnlyEntityService<T>;
  /** Data columns built by the generated file (the actions column is added here). */
  columns: GridColDef[];
  /** `{ fieldName: EnumTypeName }` for runtime enum valueOptions; omit for non-enum entities. */
  enumTypes?: Record<string, string>;
  /** Saved-view persistence key, `Module:Entity` (e.g. `Core:NumberSeries`). */
  gridKey: string;
  /** i18n key for the page title, e.g. `DisplayName:Orders`. */
  titleKey: string;
  /** Fallback label for {@link titleKey}, e.g. `Orders`. */
  title: string;
  /** Detail-route base for row-click / edit navigation, e.g. `/core/numberseries`. */
  basePath: string;
  /** Create-form route; defaults to `${basePath}/new`. */
  createPath?: string;
  /**
   * false = rows are system-produced (builders, file imports) and never hand-created:
   * no New button is rendered. Default true.
   */
  createEnabled?: boolean;
  /** Render the template split button instead of a plain New button. */
  templatesEnabled?: boolean;
  /** Enable CSV/XLSX export (requires {@link exportEndpoint}). */
  exportEnabled?: boolean;
  /** Export endpoint, e.g. `/api/core/numberSeries/export-list`. */
  exportEndpoint?: string;
  /** Export download basename, e.g. `numberSeries` (extension added per format). */
  exportFileName?: string;
  /**
   * The entity has an import adapter (`import.enabled`): the ⋮ menu offers importing a file
   * (integration_architecture.md, Online import).
   */
  importEnabled?: boolean;
  /** Initial page size (default 25). */
  defaultPageSize?: number;
  /** Initial sort model, e.g. `[{ field: 'creationTime', sort: 'desc' }]`. */
  defaultSort?: Array<{ field: string; sort: 'asc' | 'desc' }>;
  /** Initial column visibility, e.g. `{ minValue: false }`. */
  columnVisibilityModel?: Record<string, boolean>;
  /** Extra per-entity bulk actions, rendered after the built-in Delete. */
  bulkActions?: BulkAction[];
  /**
   * Bulk edit (docs/15-designspecs/Bulk-Edit-And-Actions.md): with several rows selected, Edit opens a dialog
   * that sets or clears chosen fields on every one; with one, it opens that record. Passed by the generated view only for an entity that declares
   * `ui.bulkEdit.enabled` and a user who holds its BulkEdit and Update permissions; absent, there is no button.
   */
  bulkEdit?: BulkEditConfig;
  /**
   * Commands on the selected rows (docs/15-designspecs/Bulk-Edit-And-Actions.md §4.3): the entity's `showInBulk`
   * commands the user may run, offered in the toolbar's Actions menu. Each runs once on the server over every
   * selected row; absent or empty, there is no menu.
   */
  bulkCommands?: BulkCommandConfig[];
  /** Answers which selected rows can run each of {@link bulkCommands}, e.g. `/api/sales/order/bulk/available-actions`. */
  bulkCommandsAvailabilityEndpoint?: string;
  /**
   * false = no "Group by" in the column menu, for a list whose service has no grouped-list
   * endpoint (a hand-written list over a framework table). Default true.
   */
  rowGroupingEnabled?: boolean;
}

/** Shell-owned actions column; excluded wherever "the first real column" is meant. */
const ROW_ACTIONS_FIELD = '_rowActions';
/** Server-side row grouping prefixes aggregate rows with this; they are not records. */
const GROUP_ROW_ID_PREFIX = 'group-';

export function EntityListView<T>(props: EntityListViewProps<T>) {
  const {
    module,
    entity,
    service,
    columns: dataColumns,
    enumTypes,
    gridKey,
    titleKey,
    title,
    basePath,
    createPath,
    createEnabled = true,
    templatesEnabled = false,
    exportEnabled = false,
    exportEndpoint,
    exportFileName,
    importEnabled = false,
    defaultPageSize = 25,
    defaultSort,
    columnVisibilityModel,
    bulkActions,
    bulkEdit,
    bulkCommands,
    bulkCommandsAvailabilityEndpoint,
    rowGroupingEnabled = true,
  } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();

  // Deep-link pre-filtering: `?filter.<field>=<value>` (+ optional `filterOp.<field>=<operator>`,
  // default "equals", and `filterLabel.<field>=<text>` for a human-readable chip). The filters are
  // sent as FIXED API params on every fetch — not through the grid's filter model — so a link can
  // pre-filter on any server-filterable field, including id fields the grid has no column for
  // (labels are empty/duplicated; ids are exact). Each active filter renders as a dismissible chip
  // above the grid. A saved default view's bootstrap keeps its columns/sort but must not clobber
  // this — see GridViewSelector's preserveActiveFilter.
  const [searchParams, setSearchParams] = useSearchParams();
  // Where a detail view opened from here comes back to: this list, filter included.
  const location = useLocation();
  const returnToState = useMemo(
    () => listReturnTo(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const urlFilterItems = useMemo(() => {
    const items: { field: string; operator: string; value: string; label: string }[] = [];
    searchParams.forEach((value, key) => {
      if (!key.startsWith('filter.') || value === '') return;
      const field = key.slice('filter.'.length);
      items.push({
        field,
        operator: searchParams.get(`filterOp.${field}`) ?? 'equals',
        value,
        label: searchParams.get(`filterLabel.${field}`) ?? value,
      });
    });
    return items;
  }, [searchParams]);
  const urlFilterParams = useMemo(() => {
    if (urlFilterItems.length === 0) return undefined;
    const params: Record<string, unknown> = {};
    for (const item of urlFilterItems) {
      params[item.field] = item.value;
      params[`${item.field}Operator`] = item.operator;
    }
    return params;
  }, [urlFilterItems]);
  const clearUrlFilter = useCallback((field: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(`filter.${field}`);
      next.delete(`filterOp.${field}`);
      next.delete(`filterLabel.${field}`);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  // Layout persistence + saved views are owned by GridViewSelector (rendered as the page title
  // below), so list views do not call useGridStatePersistence. Which groups are open is kept
  // apart from both: per grid on this device, never in a saved view.
  const isGroupExpandedByDefault = useGroupExpansionPersistence(apiRef, gridKey);
  const { restrictions } = useFieldRestrictions(module, entity);

  const resolvedCreatePath = createPath ?? `${basePath}/new`;

  // Server-side aggregation functions — metadata only (no client-side apply).
  const aggregationFunctions = useMemo<Record<string, GridAggregationFunctionDataSource>>(
    () => ({
      sum: { label: t('grid.sum'), columnTypes: ['number'] },
      avg: { label: t('grid.avg'), columnTypes: ['number'] },
      min: { label: t('grid.min'), columnTypes: ['number'] },
      max: { label: t('grid.max'), columnTypes: ['number'] },
    }),
    [t],
  );

  // Data-source fetch failures (paging / grouping / row load). MUI's server-side data source
  // reports these ONLY through the onDataSourceError callback — without it a failed fetch renders
  // as a silent empty "No rows" grid. Kept separate from apiValidationErrors so a later successful
  // fetch can clear the load error without wiping an unrelated bulk-action error.
  const [loadError, setLoadError] = useState<ValidationError | null>(null);

  const dataSource = useMemo<GridDataSource>(() => {
    const base = createEntityListDataSource(service, urlFilterParams);
    return {
      ...base,
      getRows: async (params) => {
        const result = await base.getRows(params);
        // Rows loaded again (e.g. refresh after the backend recovered) — retire the stale banner.
        setLoadError((prev) => (prev === null ? prev : null));
        return result;
      },
    };
  }, [service, urlFilterParams]);

  const handleDataSourceError = useCallback(
    (error: GridGetRowsError | GridUpdateRowError) => {
      console.error('List data fetch failed:', error);
      setLoadError({
        field: 'form',
        message: t('grid.loadFailed', {
          defaultValue: 'Failed to load rows: {{message}}',
          message: error.cause?.message ?? error.message,
        }),
        instancePath: '#/properties/form',
        members: ['form'],
      });
    },
    [t],
  );

  // Row selection for bulk actions. The model is normalized to concrete include-ids at change
  // time: an 'exclude' model (header select-all with server-side data) is resolved against the
  // currently loaded rows, so bulk actions only ever operate on rows the user could see.
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>(EMPTY_SELECTION);
  const [pendingBulk, setPendingBulk] = useState(false);
  // Bulk-action errors (e.g. a delete blocked by a server guard) surfaced via the app-standard banner +
  // panel — the same UX as the detail views, so a grid delete no longer fails silently.
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);
  const [validationPanelOpen, setValidationPanelOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditResult, setBulkEditResult] = useState<CommandResult | null>(null);
  const [bulkCommand, setBulkCommand] = useState<{ config: BulkCommandConfig; availability: BulkCommandAvailability | null } | null>(null);
  // Delete / bulk-action confirmation rendered via the app-standard ConfirmDialog
  // (same UX as the discard-changes dialog) instead of window.confirm.
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  } | null>(null);

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      if (model.type === 'exclude') {
        const ids = resolveSelectionIds(model, apiRef.current?.getAllRowIds() ?? []);
        setRowSelectionModel({ type: 'include', ids: new Set(ids) });
        return;
      }
      setRowSelectionModel(model);
    },
    [apiRef],
  );

  const selectedIds = useMemo(() => resolveSelectionIds(rowSelectionModel, []), [rowSelectionModel]);
  // The rows the grid holds change under an unchanged selection (a refetch after a sort, filter
  // or background refresh); bump a version on the grid's rowsSet so the selected rows below are
  // re-resolved against the current rows, not the ones from selection time.
  const [rowsVersion, setRowsVersion] = useState(0);
  useEffect(() => {
    const api = apiRef.current;
    if (!api) return undefined;
    return api.subscribeEvent('rowsSet', () => setRowsVersion((v) => v + 1));
  }, [apiRef]);
  // The picked rows themselves, for toolbar extras that read row fields (e.g. a status-gated
  // action). Resolved through the grid api from the current rows; a row that has left the grid
  // is simply absent (see resolveSelectedRows).
  const selectedRows = useMemo(
    () => resolveSelectedRows(selectedIds, (id) => apiRef.current?.getRow(id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rowsVersion is the rows' change signal
    [selectedIds, apiRef, rowsVersion],
  );

  const handleRefresh = () => {
    setRowSelectionModel(EMPTY_SELECTION);
    apiRef.current?.dataSource?.cache?.clear();
    apiRef.current?.dataSource?.fetchRows();
  };

  // Built-in Delete + any per-entity actions from the generated view.
  const allBulkActions = useMemo<BulkAction[]>(
    () => [
      ...('deleteMany' in service
        ? [{
            key: 'delete',
            label: t('common.delete'),
            icon: <Delete />,
            confirmMessage: (count: number) =>
              t('grid.confirmDeleteMultiple', {
                count,
                entity: t(`DisplayName:${entity}`, { defaultValue: entity }),
              }),
            execute: (ids: string[]) => service.deleteMany(ids),
          }]
        : []),
      ...(bulkActions ?? []),
    ],
    [t, entity, service, bulkActions],
  );

  const executeBulkAction = useCallback(
    async (action: BulkAction, ids: string[], rows: Record<string, unknown>[]) => {
      setPendingBulk(true);
      setApiValidationErrors([]);
      try {
        await action.execute(ids, rows);
        if (action.refreshAfter !== false) {
          setRowSelectionModel(EMPTY_SELECTION);
          apiRef.current?.dataSource?.cache?.clear();
          apiRef.current?.dataSource?.fetchRows();
        }
      } catch (error) {
        console.error(`Bulk action '${action.key}' failed:`, error);
        // Surface the failure (e.g. a server-side delete guard) instead of failing silently.
        const errors = getValidationErrors(error);
        setApiValidationErrors(errors.length > 0 ? errors : [{
          field: 'form',
          message: getErrorMessage(error),
          instancePath: '#/properties/form',
          members: ['form'],
        }]);
      } finally {
        setPendingBulk(false);
      }
    },
    [apiRef],
  );

  const runBulkAction = useCallback(
    (action: BulkAction) => {
      if (selectedIds.length === 0 || pendingBulk) return;
      // Snapshot the ids at click time so the action operates on what the user confirmed.
      const ids = selectedIds;
      const rows = selectedRows;
      if (action.confirmMessage) {
        setPendingConfirm({
          title: action.label,
          message: action.confirmMessage(ids.length),
          onConfirm: () => executeBulkAction(action, ids, rows),
        });
        return;
      }
      void executeBulkAction(action, ids, rows);
    },
    [selectedIds, selectedRows, pendingBulk, executeBulkAction],
  );

  // The selected rows with the stamps the grid loaded them with: a row changed since is refused (D7).
  const bulkEditRows = useMemo(
    () => selectedRows.map((row) => ({
      id: String((row as { id?: unknown }).id),
      concurrencyStamp: (row as { concurrencyStamp?: string }).concurrencyStamp,
    })),
    [selectedRows],
  );

  const handleBulkEditCompleted = useCallback((summary: BulkEditSummary) => {
    setBulkEditOpen(false);
    handleRefresh();
    // Each refused row, named by its business key, in the banner and panel — as a bulk command reports them.
    setApiValidationErrors(summary.refusals);
    setBulkEditResult(bulkEditOutcome(summary, t));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleRefresh is recreated each render and only touches the grid api
  }, [t]);

  const handleBulkCommandCompleted = useCallback((summary: BulkEditSummary) => {
    const label = bulkCommand?.config.label ?? '';
    setBulkCommand(null);
    handleRefresh();
    // Each refused row, named by its business key, in the banner and panel — as a bulk edit reports them.
    setApiValidationErrors(summary.refusals);
    setBulkEditResult(bulkCommandOutcome(summary, label, t));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleRefresh is recreated each render and only touches the grid api
  }, [t, bulkCommand]);

  const handleConfirmDialogConfirm = useCallback(async () => {
    if (!pendingConfirm) return;
    await pendingConfirm.onConfirm();
    setPendingConfirm(null);
  }, [pendingConfirm]);

  // One banner/panel for both error kinds: a data-source load failure (if any) ahead of
  // bulk-action validation errors.
  const bannerErrors = useMemo(
    () => (loadError ? [loadError, ...apiValidationErrors] : apiValidationErrors),
    [loadError, apiValidationErrors],
  );

  /**
   * Remember which record was opened from this list, so returning puts focus back on it
   * instead of the top. Session-scoped and per grid: it is a convenience for the current
   * work session, not a preference worth persisting across them.
   */
  const lastOpenedKey = `nexo-list-last-opened:${gridKey}`;
  const openRecord = useCallback(
    (id: GridRowId) => {
      try {
        sessionStorage.setItem(lastOpenedKey, String(id));
      } catch {
        // Storage can be unavailable (private mode, quota). Restoring focus is a nicety —
        // never let it stop the navigation the user actually asked for.
      }
      navigate(`${basePath}/${id}`, { state: returnToState });
    },
    [navigate, basePath, lastOpenedKey, returnToState],
  );

  // Actions column (owned by the shell) + the entity's data columns. Memoized so the DataGrid
  // columns prop keeps a stable reference across renders (a churning columns prop resets
  // user-resized widths and column order).
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: ROW_ACTIONS_FIELD,
        type: 'actions',
        headerName: t('common.actions'),
        width: 100,
        aggregable: false,
        getActions: (params) => {
          const node = apiRef.current?.getRowNode(params.id);
          if (node?.type !== 'leaf') return [];
          return [
            <GridActionsCellItem
              key="edit"
              icon={<Edit />}
              label={t('common.edit')}
              onClick={() => openRecord(params.id)}
            />,
          ];
        },
      },
      ...dataColumns,
    ],
    [t, openRecord, apiRef, dataColumns],
  );

  // Enhance columns with runtime-loaded enum valueOptions (no-op when enumTypes is empty).
  const enhancedColumns = useEnumColumns(columns, enumTypes ?? EMPTY_ENUM_TYPES);

  // Apply field restrictions (HIDDEN fields are removed from the grid).
  const restrictedColumns = useMemo(
    () => filterRestrictedColumns(enhancedColumns, restrictions),
    [enhancedColumns, restrictions],
  );

  /**
   * Keyboard-first list navigation. The grid already moves cell focus with the arrow keys —
   * that is standard DataGrid behaviour — but nothing ever gave it focus, so it only worked
   * after a click. These three pieces close that: focus the first row on arrival, open the
   * focused record with Enter, and show which row focus is on.
   */
  const firstDataField = useMemo(
    () => restrictedColumns.find((c) => c.field !== ROW_ACTIONS_FIELD)?.field,
    [restrictedColumns],
  );

  // Once per mount, not per fetch: re-focusing on every page change or refresh would yank the
  // user back to the top mid-task.
  const hasAutoFocused = useRef(false);
  const listRootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const api = apiRef.current;
    if (!api || !firstDataField) return undefined;
    return api.subscribeEvent('rowsSet', () => {
      if (hasAutoFocused.current) return;
      const ids = apiRef.current?.getAllRowIds() ?? [];
      // Group rows are not records and cannot be opened.
      const isRecord = (id: GridRowId) => !String(id).startsWith(GROUP_ROW_ID_PREFIX);

      let remembered: string | null = null;
      try {
        remembered = sessionStorage.getItem(lastOpenedKey);
      } catch { /* see openRecord */ }

      // The remembered row may legitimately be gone: deleted, filtered out, or simply on
      // another page, since pagination resets to the first page on return. Falling back to
      // the top is the right answer in all of those.
      const target =
        (remembered ? ids.find((id) => String(id) === remembered && isRecord(id)) : undefined)
        ?? ids.find(isRecord);
      if (target === undefined) return;
      if (isTypingInList(listRootRef.current, document.activeElement)) return;

      hasAutoFocused.current = true;
      // Rows are virtualised, so a restored row further down may not be mounted yet;
      // focusing an unmounted cell would do nothing visible.
      const rowIndex = apiRef.current?.getRowIndexRelativeToVisibleRows(target);
      if (typeof rowIndex === 'number' && rowIndex >= 0) {
        apiRef.current?.scrollToIndexes({ rowIndex });
      }
      apiRef.current?.setCellFocus(target, firstDataField);
    });
  }, [apiRef, firstDataField, lastOpenedKey]);

  const handleExport = async (format: 'csv' | 'xlsx') => {
    if (!exportEndpoint) return;
    try {
      // Deep-link filters shape the export exactly like the visible rows.
      const params = {
        ...buildExportRequestParams(apiRef.current!.state, restrictedColumns, format, Date.now()),
        ...urlFilterParams,
      };
      const response = await apiClient.get(exportEndpoint, {
        params,
        responseType: 'blob',
      });
      saveBlobResponse(response, `${exportFileName ?? entity}_export`);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const initialState = useMemo(() => {
    const state: Record<string, unknown> = {
      pagination: { paginationModel: { pageSize: defaultPageSize }, rowCount: 0 },
    };
    if (defaultSort && defaultSort.length) {
      state.sorting = { sortModel: defaultSort };
    }
    if (columnVisibilityModel) {
      state.columns = { columnVisibilityModel };
    }
    return state;
  }, [defaultPageSize, defaultSort, columnVisibilityModel]);

  return (
    <Box ref={listRootRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {/* Load + bulk-action error banner (same UX as the detail views) */}
      <ValidationErrorBanner
        errors={bannerErrors}
        onExpand={() => setValidationPanelOpen(true)}
        onClear={() => {
          setLoadError(null);
          setApiValidationErrors([]);
          setValidationPanelOpen(false);
        }}
      />

      {/* Toolbar with actions on left */}
      <ViewToolbar onRefresh={handleRefresh}>
        {createEnabled && (templatesEnabled ? (
          <NewTemplateSplitButton entityName={entity} createPath={resolvedCreatePath} />
        ) : (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Add />}
            onClick={() => navigate(resolvedCreatePath, { state: returnToState })}
            sx={{ textTransform: 'none', fontWeight: 500, px: 2 }}
          >
            {t('common.new')}
          </Button>
        ))}
        {bulkEdit && (
          <Button
            startIcon={<EditNote />}
            disabled={selectedIds.length === 0 || pendingBulk}
            onClick={() => {
              const target = editTarget(selectedIds);
              if (target === 'bulk') {
                setBulkEditOpen(true);
              } else {
                openRecord(target.open);
              }
            }}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {t('common.edit')}
            {selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
          </Button>
        )}
        {bulkCommands && bulkCommands.length > 0 && bulkCommandsAvailabilityEndpoint && (
          <BulkCommandsMenu
            commands={bulkCommands}
            availabilityEndpoint={bulkCommandsAvailabilityEndpoint}
            selectedIds={selectedIds}
            disabled={selectedIds.length === 0 || pendingBulk}
            onChoose={(config, availability) => setBulkCommand({ config, availability })}
          />
        )}
        {allBulkActions.map((action) => (
          <Button
            key={action.key}
            startIcon={action.icon}
            disabled={selectedIds.length === 0 || pendingBulk}
            onClick={() => runBulkAction(action)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {action.label}
            {selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
          </Button>
        ))}
        {/* Hand-written per-entity toolbar actions (see config/listViewExtras) — the list-view
            sibling of menuActionExtras/detailViewExtras, so a bespoke grid action needs no
            generator change. Renders nothing for entities without an entry. */}
        <ListViewExtras
          entity={entity}
          selectedIds={selectedIds}
          selectedRows={selectedRows}
          onActionCompleted={handleRefresh}
        />
      </ViewToolbar>

      {/* Page content + validation panel (right-side drawer, same as the detail views) */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <GridViewSelector
            apiRef={apiRef}
            gridKey={gridKey}
            defaultLabel={t(titleKey, { defaultValue: title })}
            preserveActiveFilter={urlFilterItems.length > 0}
          />
          <ListViewMoreMenu
            entity={entity}
            title={t(titleKey, { defaultValue: title })}
            basePath={basePath}
            exportEnabled={exportEnabled}
            importEnabled={importEnabled}
            onExport={handleExport}
            onImported={handleRefresh}
          />
        </Box>

        {/* Active deep-link filters: dismissible chips (delete = drop the URL param → refetch).
            Own row under the title, left-aligned — same placement as Plan Entry's version chip
            (the title row right-aligns its trailing controls, where a chip is easy to miss). */}
        {urlFilterItems.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {urlFilterItems.map((item) => (
              <Chip
                key={item.field}
                size="small"
                color="primary"
                variant="outlined"
                label={item.label}
                onDelete={() => clearUrlFilter(item.field)}
              />
            ))}
          </Box>
        )}

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <DataGridPremium
            density="compact"
            rowHeight={GRID_ROW_HEIGHT}
            apiRef={apiRef}
            columns={restrictedColumns}
            dataSource={dataSource}
            onDataSourceError={handleDataSourceError}
            aggregationFunctions={aggregationFunctions}
            disablePivoting
            disableRowGrouping={!rowGroupingEnabled}
            isGroupExpandedByDefault={isGroupExpandedByDefault}
            pagination
            onRowClick={(params) => {
              if (String(params.id).startsWith(GROUP_ROW_ID_PREFIX)) return;
              openRecord(params.id);
            }}
            onCellKeyDown={(params, event) => {
              if (event.key !== 'Enter') return;
              // Let the grid keep Enter for the things it already owns: toggling a selection
              // checkbox, expanding a group row, committing a cell edit.
              if (params.cellMode === 'edit') return;
              if (params.field === GRID_CHECKBOX_SELECTION_COL_DEF.field) return;
              if (String(params.id).startsWith(GROUP_ROW_ID_PREFIX)) return;
              event.defaultMuiPrevented = true;
              openRecord(params.id);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={initialState}
            filterDebounceMs={500}
            disableRowSelectionOnClick
            checkboxSelection
            checkboxSelectionVisibleOnly
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleSelectionChange}
            isRowSelectable={(params) => !String(params.id).startsWith(GROUP_ROW_ID_PREFIX)}
            rowSelectionPropagation={{ parents: false, descendants: false }}
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              '& .MuiDataGrid-root': { flex: 1, display: 'flex', flexDirection: 'column' },
              '& .MuiDataGrid-main': { flex: 1, overflow: 'auto' },
              '& .MuiDataGrid-virtualScroller': { flex: 1, overflow: 'auto' },
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              // Focus lands on a cell, but the record is the row — so highlight the whole row.
              // v8 tracks focus as real DOM focus rather than a class, hence :has().
              '& .MuiDataGrid-row:has(.MuiDataGrid-cell:focus)': { bgcolor: 'action.selected' },
              // Toned-down, hover-revealed row checkbox + edit pencil (shared with the child grids).
              ...hoverRevealGridSx,
            }}
          />
        </Box>
      </Box>

        {/* Validation Error Panel */}
        <ValidationErrorPanel
          open={validationPanelOpen}
          onClose={() => setValidationPanelOpen(false)}
          errors={bannerErrors}
          entity={entity}
        />
      </Box>

      {bulkEdit && (
        <BulkEditDialog
          open={bulkEditOpen}
          entity={entity}
          config={bulkEdit}
          rows={bulkEditRows}
          restrictions={restrictions}
          onClose={() => setBulkEditOpen(false)}
          onCompleted={handleBulkEditCompleted}
        />
      )}
      {bulkCommand && (
        <BulkCommandDialog
          entity={entity}
          command={bulkCommand.config}
          run={bulkCommandRun(bulkEditRows, bulkCommand.availability)}
          onClose={() => setBulkCommand(null)}
          onCompleted={handleBulkCommandCompleted}
        />
      )}
      <CommandResultSnackbar result={bulkEditResult} onClose={() => setBulkEditResult(null)} />

      {/* Delete / bulk-action confirmation (app-standard dialog, same as discard-changes) */}
      <ConfirmDialog
        open={pendingConfirm !== null}
        title={pendingConfirm?.title}
        message={pendingConfirm?.message ?? ''}
        confirmLabel={pendingConfirm?.title}
        cancelLabel={t('common.cancel')}
        confirmDisabled={pendingBulk}
        onConfirm={handleConfirmDialogConfirm}
        onCancel={() => setPendingConfirm(null)}
      />
    </Box>
  );
}
